import React, { useState, useEffect, useMemo, useContext } from 'react';
import ReactDOMServer from 'react-dom/server';
import { AuthContext } from '../../context/AuthContext';
import api from '../../api/axios';
import {
  AutoComplete, Input, Table, Button, Space, InputNumber, Form,Popconfirm,Select,
  Typography, Divider, Card, Row, Col, Empty,message
} from 'antd';
import { SearchOutlined, PlusOutlined, DeleteOutlined, PrinterOutlined } from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';

const { Text, Title } = Typography;
const { Option } = Select;
const VAT_RATE = 0.15; // 15% VAT

// --- Printable Invoice ---
const PrintableInvoice = ({ data }) => {
  const { cart = [], customer = {}, refNo, fsNo, date, shop = {} } = data || {};
  const { subtotal = 0, vatAmount = 0, grandTotal = 0 } = data.financials || {};

  const numberToWords = (num) => {
    if (num === 0) return 'Zero';
    const belowTwenty = ["","One","Two","Three","Four","Five","Six","Seven","Eight","Nine","Ten",
      "Eleven","Twelve","Thirteen","Fourteen","Fifteen","Sixteen","Seventeen","Eighteen","Nineteen"];
    const tens = ["","","Twenty","Thirty","Forty","Fifty","Sixty","Seventy","Eighty","Ninety"];
    const thousands = ["","Thousand","Million","Billion"];

    const helper = (n) => {
      if (n === 0) return "";
      if (n < 20) return belowTwenty[n] + " ";
      if (n < 100) return tens[Math.floor(n/10)] + " " + helper(n%10);
      return belowTwenty[Math.floor(n/100)] + " Hundred " + helper(n%100);
    }

    let word = "";
    let i = 0;
    while(num > 0){
      if(num % 1000 !== 0){
        word = helper(num % 1000) + thousands[i] + " " + word;
      }
      num = Math.floor(num / 1000);
      i++;
    }
    return word.trim();
  }

  const watermarkStyle = {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%) rotate(-45deg)',
    fontSize: '102px',
    fontFamily: 'Arial, sans-serif',
    color: 'rgba(200,200,200,0.2)',
    zIndex: 0,
    pointerEvents: 'none',
    whiteSpace: 'nowrap',
  };

  return (
    <html lang="en">
      <head>
        <title>Proforma</title>
        <style>{`
           @page { size: A4; margin: 5mm; }
          body { font-family: "Times New Roman", serif; font-size: 13px; margin: -10; padding: 0; position: relative; }
          .container { padding: 40px; position: relative; min-height: 297mm; }
           .topper {
    position: absolute;
    top: 5mm;
    right: 5mm;
     margin-buttom:20mm,
    font-size: 10px;
    text-align: right;
    width: 100%;
  }
          .header { font-size: 28px; font-weight: bold; text-align: left; margin: 0; margin-top:20px;  text-transform: uppercase;}
          .header2 { font-size: 14px;font-weight: bold;  text-align: left; margin: 0; text-transform: uppercase; }
          .title { font-size: 28px; text-align: center; margin: 15px 0; }
          table { width: 100%; border-collapse: collapse; margin: 10px 0; font-size: 14px; }
          th, td { border: 1px solid #000; padding: 5px; }
          .totals-table { width: 40%; float: right; margin-top: 5px; border-collapse: collapse; }
          .totals-table td { border: 1px solid #000; padding: 5px; text-align: left; }
          .footer { position: absolute; bottom: 60px; width: 100%; font-size: 14px; }
          .customer-table { width: 100%; border-collapse: separate; border-spacing: 2px; }
          .customer-table td { border: 1px solid #000; padding: 3px; font-size: 14px; }
        `}</style>
      </head>
      <body>
        <div className="container">
          <div style={watermarkStyle}>JS</div>
          <p className='topper'>{uuidv4()} | {new Date(date || Date.now()).toLocaleDateString()}</p>

          {/* Header */}
          <p className="header">{shop.header1 || 'SHOP NAME'}</p>
          <p className="header2">{shop.header2 || 'SHOP ADDRESS'}</p>
          <p className="header2">{shop.header3 || 'PHONE'}</p>
          <p className="header2">{shop.header4 || 'PHONE'}</p>
          <p className="header2">{shop.header5 || 'TIN'}</p>

          {/* Title */}
          <h2 className="title">PROFORMA</h2>

          {/* Customer Info */}
          <table className="customer-table">
            <tbody>
              <tr>
                <td>Date:</td>
                <td>{new Date(date || Date.now()).getMonth()+1}/{new Date(date || Date.now()).getDate()}/{new Date(date || Date.now()).getFullYear()}</td>
                <td>Reference:</td>
                <td>{refNo || ''}</td>
              </tr>
              <tr>
                <td>Bill To:</td>
                <td>{customer.name || ''}</td>
                <td>FS NO:</td>
                <td>{fsNo || '00000000'}</td>
              </tr>
              <tr>
                <td>TIN:</td>
                <td>{customer.tin || ''}</td>
              </tr>
              <tr>
                <td>Address:</td>
                <td>{customer.address || ''}</td>
              </tr>
            </tbody>
          </table>

          <div style={{ height: '20px' }}></div>

          {/* Items Table */}
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Description</th>
                <th>Unit</th>
                <th>Quantity</th>
                <th>Unit Price</th>
                <th>Line Total</th>
              </tr>
            </thead>
            <tbody>
              {cart.map(item => (
                <tr key={item.key}>
                  <td>{item.partNumber}</td>
                  <td>{item.partName}</td>
                  <td>{item.measurementUnit || ''}</td>
                  <td style={{ textAlign: 'right' }}>{item.quantity}</td>
                  <td style={{ textAlign: 'right' }}>{item.price.toFixed(2)}</td>
                  <td style={{ textAlign: 'right' }}>{(item.price * item.quantity).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ height: '30px' }}></div>

          {/* Totals */}
          <table className="totals-table">
            <tbody>
              <tr>
                <td>Subtotal:</td>
                <td style={{ textAlign: 'right' }}>{subtotal.toFixed(2)}</td>
              </tr>
              <tr>
                <td>VAT (Included):</td>
                <td style={{ textAlign: 'right' }}>{vatAmount.toFixed(2)}</td>
              </tr>
              <tr>
                <td><strong>Grand Total:</strong></td>
                <td style={{ textAlign: 'right' }}><strong>{grandTotal.toFixed(2)}</strong></td>
              </tr>
            </tbody>
          </table>

          <div style={{ float: 'left', marginTop: '45px'}}>
            Payment Method - <br></br>
            Amount in Words: {numberToWords(Math.round(grandTotal))} Only
          </div>

          {/* Footer */}
          <div className="footer">
            <p>Prepared by: m</p>
            <p>Approved by: Name ________________________________________________</p>
            <p>This proforma is valid for 7 days</p>
            <p><strong>Please prepare the check in the name of omar siraj ali</strong></p>
          </div>
        </div>
      </body>
    </html>
  );
};

// --- Main Proforma Component ---
const Proforma = () => {
  const { user } = useContext(AuthContext);
  const userShopId = user?.shopId;
  const userWareId = user?.warehouseId;

  const [form] = Form.useForm();
  const [inventory, setInventory] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [cart, setCart] = useState([]);
  const [selectedPart, setSelectedPart] = useState(null);
  const [shopInfo, setShopInfo] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();
const [selectedCustomerName, setSelectedCustomerName] = useState('');
const [newPart, setNewPart] = useState({
  partName: '',
  partNumber: '',
  measurementUnit: 'pcs',
});

  useEffect(() => {
    if (!userShopId) return;
    api.get('/api/spareparts').then(res => setInventory(res.data)).catch(console.error);
   
    api.get('/api/sales/customers').then(res => setCustomers(res.data)).catch(console.error);
    api.get(`/api/shops/${userShopId}/info`).then(res => setShopInfo(res.data)).catch(console.error);
  }, [userShopId  , userWareId]);
useEffect(() => {
  form.setFieldValue('proformaNo', 'P' + Math.floor(1000000000 + Math.random() * 9000000000));
}, []);

  // --- Computed totals ---
  const financials = useMemo(() => {
    const subtotal = cart.reduce((acc, item) => acc + item.price * item.quantity, 0);
    const vatAmount = subtotal * VAT_RATE;
    const grandTotal = subtotal; // VAT is included in subtotal
    return { subtotal, vatAmount, grandTotal };
  }, [cart]);

const getNextImporterId = (inventory) => {
  const jsParts = inventory
    .map(p => p.importerId)
    .filter(id => id?.startsWith('JS'))
    .map(id => parseInt(id.slice(2)))
    .filter(n => !isNaN(n));

  const lastNumber = jsParts.length > 0 ? Math.max(...jsParts) : 0;
  return `JS${lastNumber + 1}`;
};

const handleAddCustomPartWithImporter = async () => {
  try {
    const importerId = getNextImporterId(inventory);

    // Save new part to backend without image
    const { data } = await api.post('/api/spareparts', {
      importerId,
      partNumber: newPart.partNumber,
      partName: newPart.partName,
      measurementUnit: newPart.measurementUnit,
      price: 0, // default
    });

    // Add to inventory
    setInventory(prev => [...prev, data.sparepart]);

    // Add to cart with Popconfirm
    messageApi.success(`Sparepart ${newPart.partName} added with Importer ID ${importerId}`);
  } catch (err) {
    console.error(err);
    messageApi.error('Failed to add spare part');
  }
};


  // --- Add item to cart ---
  const handleAddItem = (item) => {
    if (!item) return;
    const exists = cart.find(c => c.partNumber === item.partNumber);
    if (exists) {
      setCart(cart.map(c => c.partNumber === item.partNumber ? { ...c, quantity: c.quantity + 1 } : c));
    } else {
      setCart([...cart, { ...item, key: uuidv4(), quantity: 1 }]);
    }
    setSelectedPart(null);
  };

  // --- Add custom part ---
const handleAddCustomPart = () => {
  const key = uuidv4();
  setCart([...cart, {
    key,
    partNumber: key.slice(0,6),
    partName: '',
    price: 0,
    quantity: 1,
    measurementUnit: ''
  }]);
};

  // --- Remove item from cart ---
  const handleRemoveItem = (key) => {
    setCart(cart.filter(c => c.key !== key));
  };

  // --- Handle print ---
const handleSaveAndPrint = async (values) => {
  try {
      
    const payload = {
      shopId: userShopId,
      customerName: values.customerName,
    customerTin: values.customerTin,
      subtotal: financials.subtotal,
      items: cart.map(c => ({
        sparepartId: c.id, // map from your inventory if available
        warehouseId: userWareId,
        quantity: c.quantity,
        unitPrice: c.price,
      })),
    };

    const { data: savedProforma } = await api.post('/api/proformas/create', payload);

    messageApi.success('Proforma saved successfully! Printing...');

    const printContent = ReactDOMServer.renderToStaticMarkup(
      <PrintableInvoice
        data={{
          cart,
        
               customer: { name: values.customerName, tin: values.customerTin },
          refNo: savedProforma.proformaNo,
          fsNo: form.getFieldValue('fsNo'),
          date: new Date(),
          shop: shopInfo || {},
          financials,
        }}
      />
    );

    const printWindow = window.open('', '', 'width=800,height=600');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();

  } catch (err) {
    console.error(err);
    messageApi.error('Failed to save proforma.');
  }
};


  return (
    <div style={{ padding: 20 }}>
      {contextHolder}

      <Card title="Proforma Invoice">
     


       

        <Space style={{ marginBottom: 16 }}>
          <AutoComplete
  style={{ width: 300 }}
  options={inventory.map(p => ({
    value: `[${p.importerId}]${p.partNumber} - ${p.partName}`, // display meaningful text
    partNumber: p.partNumber,
    partName: p.partName,
    importerId: p.importerId,
  }))}
  placeholder="Search spare part by Part No, Name, or Importer ID"
  value={selectedPart}
  onSelect={(value, option) => {
    const item = inventory.find(
      i =>
        i.partNumber === option.partNumber ||
        i.partName === option.partName ||
        i.importerId === option.importerId
    );
    setSelectedPart(value);
    handleAddItem(item);
  }}
  onChange={setSelectedPart}
  filterOption={(input, option) => {
    const lowerInput = input.toLowerCase();
    return (
      option.value.toLowerCase().includes(lowerInput) ||
      option.partNumber.toLowerCase().includes(lowerInput) ||
      option.partName.toLowerCase().includes(lowerInput) ||
      (option.importerId?.toLowerCase() || '').includes(lowerInput)
    );
  }}
/>

  <Popconfirm
  title={
    <div>
      <p>Confirm adding the part to cart?</p>
      <Input
        placeholder="Part Name"
        value={newPart.partName}
        onChange={e => setNewPart({ ...newPart, partName: e.target.value })}
        style={{ marginBottom: 8 }}
      />
      <Input
        placeholder="Part Number"
        value={newPart.partNumber}
        onChange={e => setNewPart({ ...newPart, partNumber: e.target.value })}
        style={{ marginBottom: 8 }}
      />
      <Select
        value={newPart.measurementUnit}
        onChange={value => setNewPart({ ...newPart, measurementUnit: value })}
        style={{ width: '100%' }}
      >
        <Option value="pcs">pcs</Option>
        <Option value="set">set</Option>
      </Select>
    </div>
  }
  onConfirm={handleAddCustomPartWithImporter}
  okText="Add"
  cancelText="Cancel"
>
  <Button icon={<PlusOutlined />}>Add Custom Part</Button>
</Popconfirm>


        </Space>

        <Table
          dataSource={cart}
          pagination={false}
          rowKey="key"
          bordered
        >
          <Table.Column title="ID" dataIndex="partNumber" key="partNumber" />
          <Table.Column title="Description" dataIndex="partName" key="partName"
            render={(text, record) => (
              <Input
                value={text}
                onChange={(e) => setCart(cart.map(c => c.key === record.key ? { ...c, partName: e.target.value } : c))}
              />
            )}
          />
          <Table.Column title="Unit" dataIndex="measureUnit" key="measureUnit"
            render={(text, record) => (
              <Input
                value={text}
                onChange={(e) => setCart(cart.map(c => c.key === record.key ? { ...c, measureUnit: e.target.value } : c))}
              />
            )}
          />
          <Table.Column title="Quantity" dataIndex="quantity" key="quantity"
            render={(text, record) => (
              <InputNumber min={1} value={text} onChange={(value) => setCart(cart.map(c => c.key === record.key ? { ...c, quantity: value } : c))} />
            )}
          />
          <Table.Column title="Unit Price" dataIndex="price" key="price"
            render={(text, record) => (
              <InputNumber min={0} value={text} onChange={(value) => setCart(cart.map(c => c.key === record.key ? { ...c, price: value } : c))} />
            )}
          />
         <Table.Column title="Line Total" key="lineTotal"
  render={(text, record) => (record.price * record.quantity).toFixed(2)}
/>

          <Table.Column title="Action" key="action"
            render={(text, record) => (
              <Button danger icon={<DeleteOutlined />} onClick={() => handleRemoveItem(record.key)} />
            )}
          />
        </Table>

        <Divider />

        <Row justify="end">
          <Col span={8}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <tbody>
                <tr>
                  <td>Subtotal:</td>
                  <td style={{ textAlign: 'right' }}>{financials.subtotal.toFixed(2)}</td>
                </tr>
                <tr>
                  <td>VAT (Included 15%):</td>
                  <td style={{ textAlign: 'right' }}>{financials.vatAmount.toFixed(2)}</td>
                </tr>
                <tr>
                  <td><strong>Grand Total:</strong></td>
                  <td style={{ textAlign: 'right' }}><strong>{financials.grandTotal.toFixed(2)}</strong></td>
                </tr>
              </tbody>
            </table>
          </Col>
        </Row>

        <Divider />
  <Form form={form} layout="vertical" onFinish={handleSaveAndPrint}>
  <Row gutter={16}>
    <Col span={12}>
      <Form.Item name="customerName" label="Customer" rules={[{ required: true, message: 'Customer is required' }]}>
        <AutoComplete
          options={customers.map(c => ({ value: c.name, tin: c.tin }))}
          placeholder="Select Customer"
          onSelect={(val, option) => {
            // This sets both customerName and customerTin based on selection
            form.setFieldsValue({
              customerName: option.value,
              customerTin: option.tin,
            });
          }}
          filterOption={(input, option) =>
            option.value.toLowerCase().includes(input.toLowerCase())
          }
          style={{ width: '100%' }}
        />
      </Form.Item>
    </Col>

    <Col span={12}>
      <Form.Item
        name="customerTin"
        label="TIN"
        rules={[{ required: false }]} // Optional field
      >
        <Input placeholder="Customer TIN" />
      </Form.Item>
    </Col>
  </Row>

  {/* Hidden Proforma Number */}
  <Form.Item name="proformaNo" label="Proforma Number" hidden>
    <Input defaultValue={`P${Math.floor(1000000000 + Math.random() * 9000000000)}`} />
  </Form.Item>

  <Row justify="end">
    <Col span={12}>
      <Button type="primary" icon={<PrinterOutlined />} htmlType="submit">
        Save and Print Proforma
      </Button>
    </Col>
  </Row>
</Form>


     
      </Card>
      
    </div>
  );
};

export default Proforma;

